import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  Handle,
  Node,
  NodeProps,
  Position,
  addEdge,
  useEdgesState,
  useNodesState,
} from 'reactflow';
// @ts-ignore
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import dagre from 'dagre';
import { FiGitBranch, FiInbox, FiLayers, FiPieChart } from 'react-icons/fi';
import { GoTag } from 'react-icons/go';
import { MdAccessTime, MdOutlineMail } from 'react-icons/md';
import { RiDraggable } from 'react-icons/ri';
import { useSelector } from 'react-redux';
// @ts-ignore
import 'reactflow/dist/style.css';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../../app/store';
import DetailPanel from '../../components/DetailPanel';

// Type assertions for icon components
const RiDraggableComponent = RiDraggable as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const GoTagComponent = GoTag as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const MdEmailComponent = MdOutlineMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const MdAccessTimeComponent = MdAccessTime as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiInboxComponent = FiInbox as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiGitBranchComponent = FiGitBranch as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiLayersComponent = FiLayers as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPieChartComponent = FiPieChart as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// --- Types ---
interface Condition {
  category: string;
  operator: string;
  value: string;
}

interface Bucket {
  name: string;
  weight: number;
  to?: string;
}

interface MultiSplitRule {
  id: string;
  condition: string | any;
  to: string;
  label: string;
}

interface NodeData {
  label: string;
  id: string;
  maxWaitDisplay?: string;
  type?: string;
  tag?: string | string[];
  selectedTags?: string[];
  template?: string;
  variantId?: string;
  variables?: { name: string; address?: string };
  trigger?: string;
  onNodeClick?: (node: Node) => void;
  onDeleteConnection?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  delayType?: 'fixed' | 'specific';
  amount?: number;
  unit?: 'days' | 'weeks' | 'months';
  date?: string;
  time?: string;
  conditions?: Condition[];
  maxWaitAmount?: number;
  maxWaitUnit?: string;
  buckets?: Bucket[];
  isExit?: boolean;
  isBranch?: boolean;
  branchConditions?: string[];
  isMultiSplit?: boolean;
  multiSplitRules?: MultiSplitRule[];
  defaultTo?: string;
}

interface LayoutedElements {
  nodes: Node<NodeData>[];
  edges: Edge[];
}

interface HierarchyGraphFlowProps {
  initialNodes?: Array<{
    id: string;
    label: string;
    type?: string;
    position: { x: number; y: number };
    tag?: string | string[];
    selectedTags?: string[];
    template?: string;
    variantId?: string;
    variables?: { name: string; address?: string };
    delayType?: string;
    amount?: number;
    unit?: string;
    date?: string;
    time?: string;
    maxWaitAmount?: number;
    maxWaitUnit?: string;
    conditions?: any[];
    buckets?: Bucket[];
    isMultiSplit?: boolean;
    multiSplitRules?: MultiSplitRule[];
    defaultTo?: string;
    isBranch?: boolean;
    branchConditions?: string[];
  }>;
  initialEdges?: Array<{ id: string; source: string; target: string; sourceHandle?: string }>;
  automation?: {
    trigger?: string;
  };
  isViewMode?: boolean;
}

// Helper to get the order of handles for proper layout
const getHandleOrder = (sourceNode: Node<NodeData> | undefined, handleId?: string | null): number => {
  if (!handleId || !sourceNode) return 0;

  // Branch node handles
  if (handleId === 'true') return 0;
  if (handleId === 'false') return 1;

  // Percentage split handles
  if (handleId.startsWith('percent-')) {
    const idx = parseInt(handleId.replace('percent-', ''), 10);
    return isNaN(idx) ? 0 : idx;
  }

  // Multi split handles
  if (handleId === 'default') return 0;
  if (handleId.startsWith('rule-')) {
    const ruleId = handleId.replace('rule-', '');
    const idx = sourceNode.data.multiSplitRules?.findIndex((r) => r.id === ruleId);
    return idx !== undefined && idx !== -1 ? idx + 1 : 0;
  }

  return 0;
};

// Arranges nodes and edges using the Dagre library for a top-to-bottom layout
const defaultNodeWidth = 200;
const defaultNodeHeight = 60;
const homeNodeWidth = 300;
const homeNodeHeight = 200;
const branchNodeWidth = 280;
const branchNodeHeight = 110;
const multiSplitNodeWidth = 320;
const multiSplitNodeHeight = 180;

export const getLayoutedElements = (
  nodes: Node<NodeData>[],
  edges: Edge[]
): LayoutedElements => {
  // Create a copy of nodes to work with
  const workingNodes = [...nodes];
  const workingEdges = [...edges];

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB',
    ranksep: 100,
    nodesep: 100,
    marginx: 50,
    marginy: 50,
  });

  const nodeMap = new Map<string, Node<NodeData>>();
  workingNodes.forEach((node) => {
    nodeMap.set(node.id, node);
    let width = defaultNodeWidth;
    let height = defaultNodeHeight;

    if (node.id === 'home') {
      width = homeNodeWidth;
      height = homeNodeHeight;
    } else if (node.data.isExit) {
      height = 40;
      width = 120;
    } else if (node.data.isBranch) {
      width = branchNodeWidth;
      height = branchNodeHeight;
    } else if (node.data.isMultiSplit || node.data.type === 'multi') {
      width = multiSplitNodeWidth;
      const rulesCount = node.data.multiSplitRules?.length || 3;
      height = multiSplitNodeHeight + rulesCount * 20;
    } else if (node.data.type === 'percent') {
      width = 240;
      height = 100;
    } else if (node.data.type === 'wait' && (node.data.conditions?.length ?? 0) > 0) {
      height += 30;
    }

    dagreGraph.setNode(node.id, { width, height });
  });

  // Sort edges by handle order for consistent layout
  const sortedEdges = [...workingEdges].sort((a, b) => {
    if (a.source === b.source) {
      const sourceNode = nodeMap.get(a.source);
      return getHandleOrder(sourceNode, a.sourceHandle) - getHandleOrder(sourceNode, b.sourceHandle);
    }
    return 0;
  });

  // Build ordered edges for dagre
  const outEdgesMap = new Map<string, Edge[]>();
  sortedEdges.forEach((edge) => {
    if (!outEdgesMap.has(edge.source)) outEdgesMap.set(edge.source, []);
    outEdgesMap.get(edge.source)!.push(edge);
  });

  const visited = new Set<string>();
  const queue: string[] = ['home'];
  const orderedEdges: Edge[] = [];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    const outEdges = outEdgesMap.get(currentId) || [];
    outEdges.forEach((edge) => {
      orderedEdges.push(edge);
      if (!visited.has(edge.target)) queue.push(edge.target);
    });
  }

  // Add any remaining edges not visited
  sortedEdges.forEach((edge) => {
    if (!orderedEdges.some((e) => e.id === edge.id)) {
      orderedEdges.push(edge);
    }
  });

  orderedEdges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  // Process branch sources to align branches properly
  const processedBranchSources = new Set<string>();

  const getSubtreeNodeIds = (rootId: string): string[] => {
    const result: string[] = [];
    const q = [rootId];
    const seen = new Set<string>();
    while (q.length > 0) {
      const curr = q.shift()!;
      if (seen.has(curr)) continue;
      seen.add(curr);
      result.push(curr);
      orderedEdges.forEach((e) => {
        if (e.source === curr && !seen.has(e.target)) q.push(e.target);
      });
    }
    return result;
  };

  // Find all branch nodes (nodes with multiple outgoing handles)
  const branchNodes = new Set<string>();
  const edgeGroups: { [key: string]: Edge[] } = {};

  orderedEdges.forEach((edge) => {
    if (edge.sourceHandle) {
      if (!edgeGroups[edge.source]) edgeGroups[edge.source] = [];
      edgeGroups[edge.source].push(edge);
    }
  });

  Object.keys(edgeGroups).forEach((sourceId) => {
    if (edgeGroups[sourceId].length >= 2) {
      branchNodes.add(sourceId);
    }
  });

  // Process each branch node
  branchNodes.forEach((sourceId) => {
    const sourceNode = nodeMap.get(sourceId);
    const branchEdges = edgeGroups[sourceId].sort((a, b) =>
      getHandleOrder(sourceNode, a.sourceHandle) - getHandleOrder(sourceNode, b.sourceHandle)
    );

    if (branchEdges.length < 2) return;
    processedBranchSources.add(sourceId);

    // Get the subtrees for each branch
    const subtrees = branchEdges.map((e) => ({
      nodeId: e.target,
      handleOrder: getHandleOrder(sourceNode, e.sourceHandle),
      rootX: dagreGraph.node(e.target)?.x ?? 0,
      nodeIds: getSubtreeNodeIds(e.target),
    }));

    // Get current X positions sorted
    const sortedXPositions = [...subtrees].sort((a, b) => a.rootX - b.rootX).map((s) => s.rootX);

    // Get desired order based on handle order
    const desiredOrder = [...subtrees].sort((a, b) => a.handleOrder - b.handleOrder);

    // Check if already in correct order
    if (desiredOrder.every((s, i) => Math.abs(s.rootX - sortedXPositions[i]) < 5)) return;

    // Calculate spacing between branches
    const minX = sortedXPositions[0];
    const maxX = sortedXPositions[sortedXPositions.length - 1];
    const totalWidth = maxX - minX;
    const spacing = totalWidth / (desiredOrder.length - 1);

    // Reposition subtrees in desired order
    desiredOrder.forEach((subtree, i) => {
      const targetX = minX + (i * spacing);
      const delta = targetX - subtree.rootX;
      if (Math.abs(delta) < 1) return;

      subtree.nodeIds.forEach((nodeId) => {
        const pos = dagreGraph.node(nodeId);
        if (pos) {
          pos.x += delta;
        }
      });
    });
  });

  workingNodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    if (nodeWithPosition) {
      node.position = {
        x: nodeWithPosition.x - nodeWithPosition.width / 2,
        y: nodeWithPosition.y - nodeWithPosition.height / 2,
      };
    }
  });

  return { nodes: workingNodes, edges: orderedEdges };
};

// Renders the home node representing the automation trigger
const CustomHomeNode: React.FC<NodeProps<NodeData>> = ({
  data,
  id,
  selected,
}) => {
  const trigger = data.trigger || '';

  const triggerLabels: Record<string, string> = {
    form_submission: 'Form Submission',
    new_subscriber: 'New Subscriber',
    email_opened: 'Email Opened',
    link_clicked: 'Link Clicked',
    scheduled_date_time: 'Scheduled Date/Time',
  };

  const conditions = useSelector(
    (state: RootState) => state.automation.conditions || []
  );
  const conditionCount = conditions?.length || 0;

  return (
    <div
      className={`relative p-3 rounded bg-white shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'
        }`}
      style={{ width: '260px' }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        id="home-bottom"
        style={{
          bottom: '-5px',
          background: '#000',
          width: '10px',
          height: '10px',
        }}
      />
      <div className="flex items-start gap-2 mb-1">
        <FiInboxComponent className="text-gray-600 text-lg mt-[5px]" />
        <div>
          <span className="font-semibold text-gray-800 text-sm">Trigger</span>
          <div className="text-gray-600 text-xs">
            {trigger ? triggerLabels[trigger] : 'No Trigger Selected'}
          </div>
        </div>
      </div>

      {conditionCount > 0 && (
        <div className="absolute -bottom-[1.3rem] left-0 bg-[#00A4BD] text-white text-[10px] font-medium rounded px-2 py-0.5 shadow-md whitespace-nowrap z-10">
          {conditionCount} Condition{conditionCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

// Renders a Branch node
const BranchNode: React.FC<NodeProps<NodeData>> = ({ data, selected }) => {
  // const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div
      className={`relative bg-white rounded-lg shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'
        }`}
      style={{ width: '280px' }}
      onClick={(e) => {
        e.stopPropagation();
        if (data.onNodeClick) {
          data.onNodeClick({ id: data.id, data } as Node<NodeData>);
        }
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{
          top: '-5px',
          background: '#000',
          width: '10px',
          height: '10px',
        }}
      />

      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <FiGitBranchComponent className="text-sm" />
          <span className="font-semibold text-gray-800 text-sm">
            True/False Branch
          </span>
        </div>

      </div>

      <div className="flex justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <div className="relative flex flex-col items-center">
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            style={{
              background: '#10B981',
              width: '12px',
              height: '12px',
              position: 'relative',
              marginBottom: '2px',
              border: '2px solid white',
            }}
          />
          <span className="text-[11px] font-semibold text-green-600">True</span>
        </div>
        <div className="relative flex flex-col items-center">
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            style={{
              background: '#EF4444',
              width: '12px',
              height: '12px',
              position: 'relative',
              marginBottom: '2px',
              border: '2px solid white',
            }}
          />
          <span className="text-[11px] font-semibold text-red-500">False</span>
        </div>
      </div>
    </div>
  );
};

// Percentage Split Node
const PercentageSplitNode: React.FC<NodeProps<NodeData>> = ({ data, selected }) => {
  const buckets = data.buckets || [{ name: 'A', weight: 50 }, { name: 'B', weight: 50 }];

  return (
    <div
      className={`relative bg-white rounded-lg shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'
        }`}
      style={{ width: '240px' }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (data.onNodeClick) {
          data.onNodeClick({ id: data.id, data } as Node<NodeData>);
        }
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{ top: '-5px', background: '#000', width: '10px', height: '10px' }}
      />
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center">
            <FiPieChartComponent className="text-sm" />
          </div>
          <span className="font-semibold text-gray-800 text-sm">Percentage Split</span>
        </div>

      </div>
      <div className="flex justify-center items-center gap-6 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        {buckets.map((bucket, index) => {
          const handleColor = index === 0 ? '#3B82F6' : '#10B981';
          return (
            <div key={index} className="relative flex flex-col items-center">
              <Handle
                type="source"
                position={Position.Bottom}
                id={`percent-${index}`}
                style={{
                  background: handleColor,
                  width: '10px',
                  height: '10px',
                  position: 'relative',
                  marginBottom: '6px',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
              <span className="text-[11px] font-medium text-center whitespace-nowrap" style={{ color: handleColor }}>
                {bucket.name} ({bucket.weight}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Multi Split Node
const MultiSplitNode: React.FC<NodeProps<NodeData>> = ({ id, data, selected }) => {
  const rawRules = data.multiSplitRules || [{ id: 'default', condition: '', to: '', label: 'Default' }];

  // Apply short labels when more than 4 rules to prevent overflow
  const useShortLabels = rawRules.length > 4;
  const rules = rawRules.map((rule, idx) => ({
    ...rule,
    label: idx === 0
      ? 'Default'
      : (useShortLabels ? `C${idx}` : rule.label),
  }));

  // Dynamically adjust text size based on total rules count
  const textSize = rules.length > 6 ? 'text-[8px]' : rules.length > 4 ? 'text-[9px]' : 'text-[11px]';

  return (
    <div
      className={`relative bg-white rounded-lg shadow-md border ${selected ? 'ring-2 ring-[#00A4BD]' : 'border-gray-300'
        }`}
      style={{ width: '340px', minWidth: '340px' }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (data.onNodeClick) {
          data.onNodeClick({ id, data } as Node<NodeData>);
        }
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{ top: '-5px', background: '#000', width: '10px', height: '10px' }}
      />
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center">
            <FiLayersComponent className="text-sm" />
          </div>
          <span className="font-semibold text-gray-800 text-sm">Multi Split</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">{rawRules.length} rules</span>
        </div>
      </div>
      <div className="flex justify-between items-center gap-0.5 px-2 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg overflow-hidden">
        {rules.map((rule, index) => {
          // Different colors for different rule types
          let handleColor = '#3B82F6';
          if (rule.label === 'Default') {
            handleColor = '#9CA3AF';
          } else if (index === 1) {
            handleColor = '#10B981';
          } else if (index === 2) {
            handleColor = '#8B5CF6';
          } else {
            handleColor = '#F59E0B';
          }

          const handleId = rule.id === 'default' ? 'default' : `rule-${rule.id}`;

          return (
            <div key={rule.id} className="relative flex flex-col items-center justify-end flex-1 min-w-0">
              <Handle
                type="source"
                position={Position.Bottom}
                id={handleId}
                style={{
                  // Reset React Flow's default absolute positioning
                  position: 'relative',
                  left: 'auto',
                  right: 'auto',
                  top: 'auto',
                  bottom: 'auto',
                  // Center the handle horizontally above the label
                  display: 'block',
                  margin: '0 auto 4px auto',
                  background: handleColor,
                  width: '8px',
                  height: '8px',
                  border: '2px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
              <span
                className={`${textSize} font-medium text-center whitespace-nowrap`}
                style={{ color: handleColor }}
              >
                {rule.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Renders a custom node for actions, delays, or conditions in the flow
const CustomNode: React.FC<NodeProps<NodeData>> = ({ data, selected }) => {
  let IconComponent = null;
  let subText = null;

  if (data.type === 'tags') {
    IconComponent = GoTagComponent;
    if (data.selectedTags?.length) {
      subText = data.selectedTags.join(', ');
    } else if (typeof data.tag === 'string') {
      subText = data.tag;
    } else if (Array.isArray(data.tag)) {
      subText = data.tag.join(', ');
    } else {
      subText = 'No Tags';
    }
  } else if (data.type === 'send') {
    IconComponent = MdEmailComponent;
    subText = null;
  } else if (data.type === 'time') {
    IconComponent = MdAccessTimeComponent;
    if (data.delayType === 'fixed' && data.amount && data.unit) {
      subText = `${data.amount} ${data.amount === 1
        ? data.unit.slice(0, -1)
        : data.unit.charAt(0).toUpperCase() + data.unit.slice(1)
        }`;
    } else if (data.delayType === 'specific' && data.date && data.time) {
      subText = `${data.date} ${data.time}`;
    } else {
      subText = 'Set time delay';
    }
  } else if (data.type === 'wait') {
    IconComponent = MdAccessTimeComponent;
    // Use maxWaitDisplay if available, otherwise fallback to maxWaitAmount/maxWaitUnit
    if (data.maxWaitDisplay) {
      subText = data.maxWaitDisplay;
    } else if (data.maxWaitAmount && data.maxWaitUnit) {
      const unitDisplay = data.maxWaitAmount === 1
        ? data.maxWaitUnit.slice(0, -1)
        : data.maxWaitUnit.charAt(0).toUpperCase() + data.maxWaitUnit.slice(1);
      subText = `${data.maxWaitAmount} ${unitDisplay}`;
    } else {
      subText = 'Set Condition';
    }
  } else if (data.isExit) {
    return (
      <div
        className={`relative p-2 bg-gray-100 rounded shadow-sm w-28 text-xs text-center ${selected ? 'ring-2 ring-[#00A4BD]' : ''
          }`}
        style={{ border: '1px solid #d1d5db' }}
      >
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{
            top: '-5px',
            background: '#000',
            width: '10px',
            height: '10px',
          }}
        />
        <span className="font-semibold text-gray-600">{data.label}</span>
      </div>
    );
  }

  return (
    <div
      className={`relative p-3 bg-white rounded shadow-md w-44 text-xs cursor-pointer ${selected ? 'ring-2 ring-[#00A4BD]' : ''
        }`}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (data.onNodeClick) {
          data.onNodeClick({ id: data.id, data } as Node<NodeData>);
        }
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{
          top: '-5px',
          background: '#000',
          width: '10px',
          height: '10px',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{
          bottom: '-5px',
          background: '#000',
          width: '10px',
          height: '10px',
        }}
      />
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-2">
          {IconComponent && (
            <IconComponent className="text-gray-600 text-lg" />
          )}
          <div>
            <div className="font-semibold">{data.label}</div>
            {subText && (
              <div className="text-gray-600 text-xs mt-1 truncate max-w-[120px]">
                {subText}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Conditions badge */}
      {data.type === 'wait' &&
        Array.isArray(data.conditions) &&
        data.conditions.length > 0 && (
          <div className="text-white border border-[#00A4BD] bg-[#00A4BD] text-[11px] rounded rounded-tl-none px-1 py-0.5 absolute top-[55px] left-[0.1px] z-10">
            {data.conditions.length} Condition
            {data.conditions.length > 1 ? 's' : ''}
          </div>
        )}
    </div>
  );
};

// Defines node types for ReactFlow
const nodeTypes = {
  customHome: CustomHomeNode,
  custom: CustomNode,
  branch: BranchNode,
  multiSplit: MultiSplitNode,
  percentSplit: PercentageSplitNode,
};

// Renders a draggable sidebar item for adding nodes to the flow
interface SidebarItemProps {
  type: string;
  label: string;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ type, label }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-item-${type}-${label}`,
    data: { type, label },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`flex relative items-center p-2 mb-2 bg-[#F5F8FA] rounded cursor-move text-[14px] text-[#3F5265] ${isDragging ? 'opacity-50 border-blue-500' : ''
        }`}
    >
      <div className="mr-2">
        <RiDraggableComponent />
      </div>
      {label}
    </div>
  );
};

// Wraps the ReactFlow canvas to make it droppable for drag-and-drop functionality
interface DroppableCanvasProps {
  children: React.ReactNode;
  id: string;
}

const DroppableCanvas: React.FC<DroppableCanvasProps> = ({ children, id }) => {
  const { setNodeRef } = useDroppable({
    id,
  });

  return (
    <div ref={setNodeRef} className="flex-1 h-[650px]">
      {children}
    </div>
  );
};

// Main component for rendering and managing the automation flow graph
const HierarchyGraphFlow = forwardRef<
  { nodes: Node<NodeData>[]; edges: Edge[] },
  HierarchyGraphFlowProps
>(
  (
    { initialNodes = [], initialEdges = [], automation, isViewMode = false },
    ref
  ) => {
    // Formats initial nodes to match ReactFlow's expected structure
    const formattedInitialNodes: Node<NodeData>[] = useMemo(() => {
      return initialNodes.map((node) => {
        let label = node.label;
        if (node.type === 'send') {
          label = 'Send Email';
        }

        // Determine if this is a multi split or percent node
        const isMultiSplit = node.isMultiSplit || node.type === 'multi' || false;
        const isPercent = node.type === 'percent' || false;

        // Determine node type for ReactFlow
        let rfType = 'custom';
        if (node.id === 'home') {
          rfType = 'customHome';
        } else if (node.type === 'branch' || node.isBranch) {
          rfType = 'branch';
        } else if (isMultiSplit) {
          rfType = 'multiSplit';
        } else if (isPercent) {
          rfType = 'percentSplit';
        }

        return {
          id: node.id,
          type: rfType,
          data: {
            label: label,
            id: node.id,
            type: node.type,
            tag: node.tag || '',
            selectedTags: node.selectedTags || [],
            template: node.template || '',
            variantId: node.variantId || node.template || '',
            variables: node.variables || { name: '' },
            trigger: automation?.trigger || '',
            delayType:
              node.delayType === 'fixed' || node.delayType === 'specific'
                ? node.delayType
                : undefined,
            amount: node.amount || 0,
            unit:
              node.unit === 'days' ||
                node.unit === 'weeks' ||
                node.unit === 'months'
                ? node.unit
                : undefined,
            date: node.date || '',
            time: node.time || '',
            conditions: node.conditions || [],
            maxWaitAmount: node.maxWaitAmount || 0,
            maxWaitUnit: node.maxWaitUnit || '',
            buckets: node.buckets || [],
            isExit: node.id === 'exit' || node.type === 'exit',
            isBranch: node.type === 'branch' || node.isBranch || false,
            isMultiSplit: isMultiSplit,
            multiSplitRules: node.multiSplitRules || [{ id: 'default', condition: '', to: '', label: 'Default' }],
            defaultTo: node.defaultTo || '',
            branchConditions: node.branchConditions || [],
            onNodeClick: (node) => setSelectedNode(node),
            onDeleteConnection: isViewMode
              ? undefined
              : (nodeId) =>
                setEdges((eds) =>
                  eds.filter(
                    (edge) =>
                      edge.source !== nodeId && edge.target !== nodeId
                  )
                ),
            onDeleteNode: isViewMode
              ? undefined
              : (nodeId) => {
                if (nodeId !== 'home' && nodeId !== 'exit') {
                  setNodes((nds) =>
                    nds.filter((node) => node.id !== nodeId)
                  );
                  setEdges((eds) =>
                    eds.filter(
                      (edge) =>
                        edge.source !== nodeId && edge.target !== nodeId
                    )
                  );
                }
              },
          },
          position: node.position || { x: 0, y: 0 },
        };
      });
    }, [initialNodes, isViewMode, automation]);

    // Formats initial edges to match ReactFlow's expected structure
    const formattedInitialEdges: Edge[] = useMemo(() => {
      return initialEdges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        style: {
          stroke: '#D000E5',
          strokeWidth: 2,
          filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))',
        },
        animated: true,
      }));
    }, [initialEdges]);

    const [nodes, setNodes, onNodesChange] = useNodesState<NodeData>(
      formattedInitialNodes.length > 0
        ? formattedInitialNodes
        : [
          {
            id: 'home',
            type: 'customHome',
            data: {
              label: 'Trigger',
              id: 'home',
              trigger: automation?.trigger || '',
              onNodeClick: (node) => setSelectedNode(node),
              onDeleteConnection: isViewMode
                ? undefined
                : (nodeId) =>
                  setEdges((eds) =>
                    eds.filter(
                      (edge) =>
                        edge.source !== nodeId && edge.target !== nodeId
                    )
                  ),
            },
            position: { x: 0, y: 0 },
          },
        ]
    );

    const [edges, setEdges, onEdgesChange] = useEdgesState(
      formattedInitialEdges
    );

    const [selectedNode, setSelectedNode] = useState<Node<NodeData> | null>(
      null
    );
    const [activeItem, setActiveItem] = useState<{
      type: string;
      label: string;
    } | null>(null);

    // Prevent running layout more than once on mount
    const isInitialLayoutDone = useRef(false);

    // Apply dagre layout on first meaningful load
    useEffect(() => {
      if (isInitialLayoutDone.current) return;
      if (nodes.length <= 1) return;

      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(nodes, edges);

      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      isInitialLayoutDone.current = true;
    }, [nodes.length, edges.length]);

    // Auto-select the home node when in view mode
    useEffect(() => {
      if (isViewMode && nodes.length > 0) {
        const homeNode = nodes.find((node) => node.id === 'home');
        if (homeNode) {
          setSelectedNode(homeNode);
        }
      }
    }, [isViewMode, nodes]);

    // Exposes nodes and edges to parent components via ref
    useImperativeHandle(ref, () => ({
      nodes,
      edges,
    }));

    // Handles connecting two nodes with an edge
    const onConnect = useCallback(
      (params: Connection) => {
        if (isViewMode) return;
        if (params.target === 'home') return;

        const existingEdge = edges.find(
          (edge) => edge.target === params.target
        );
        if (existingEdge) {
          setEdges((eds) =>
            eds.filter((edge) => edge.target !== params.target)
          );
        }

        const newEdge: Edge = {
          id: `e${params.source!}-${params.target!}`,
          ...params,
          source: params.source!,
          target: params.target!,
          style: {
            stroke: '#D000E5',
            strokeWidth: 2,
            filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))',
          },
          animated: true,
        };
        setEdges((eds) => addEdge(newEdge, eds));
      },
      [setEdges, isViewMode, edges]
    );

    // Handles the start of a drag event for a sidebar item
    const handleDragStart = useCallback(
      (event: DragStartEvent) => {
        if (isViewMode) return;
        setActiveItem(
          event.active.data.current as { type: string; label: string }
        );
      },
      [isViewMode]
    );

    // Handles the end of a drag event, adding a new node and edge if dropped on the canvas
    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        if (isViewMode) return;
        setActiveItem(null);

        if (event.over?.id !== 'reactflow-canvas') return;

        const { label, type } = event.active.data.current as {
          label: string;
          type: string;
        };

        if (type === 'exit') return;

        const newNodeId = uuidv4();
        const isBranch = type === 'branch';
        const isMultiSplit = type === 'multi';
        const isPercent = type === 'percent';

        let rfType = 'custom';
        if (isBranch) rfType = 'branch';
        else if (isMultiSplit) rfType = 'multiSplit';
        else if (isPercent) rfType = 'percentSplit';

        const newNode: Node<NodeData> = {
          id: newNodeId,
          type: rfType,
          data: {
            label,
            type,
            id: newNodeId,
            tag: type === 'tags' ? '' : undefined,
            selectedTags: type === 'tags' ? [] : undefined,
            template: type === 'send' ? '' : undefined,
            variantId: type === 'send' ? '' : undefined,
            variables: type === 'send' ? { name: '' } : undefined,
            delayType: type === 'time' ? undefined : undefined,
            amount: type === 'time' ? 0 : undefined,
            unit: type === 'time' ? undefined : undefined,
            date: type === 'time' ? '' : undefined,
            time: type === 'time' ? '' : undefined,
            conditions: type === 'wait' ? [] : undefined,
            maxWaitAmount: type === 'wait' ? 0 : undefined,
            maxWaitUnit: type === 'wait' ? '' : undefined,
            buckets:
              type === 'percent'
                ? [
                  { name: 'A', weight: 50 },
                  { name: 'B', weight: 50 },
                ]
                : undefined,
            isBranch: isBranch,
            branchConditions: isBranch ? [''] : undefined,
            isMultiSplit: isMultiSplit,
            multiSplitRules: isMultiSplit ? [{ id: 'default', condition: '', to: '', label: 'Default' }] : undefined,
            defaultTo: isMultiSplit ? '' : undefined,
            onNodeClick: (node) => setSelectedNode(node),
            onDeleteConnection: (nodeId) =>
              setEdges((eds) =>
                eds.filter(
                  (edge) => edge.source !== nodeId && edge.target !== nodeId
                )
              ),
            onDeleteNode: (nodeId) => {
              if (nodeId !== 'home' && nodeId !== 'exit') {
                setNodes((nds) => nds.filter((node) => node.id !== nodeId));
                setEdges((eds) =>
                  eds.filter(
                    (edge) =>
                      edge.source !== nodeId && edge.target !== nodeId
                  )
                );
              }
            },
          },
          position: { x: 0, y: 0 },
        };

        // Find the last non-exit node to connect to
        const nonExitNodes = nodes.filter(
          (n) => !n.data.isExit && n.id !== 'home'
        );
        const lastNode =
          nonExitNodes.length > 0
            ? nonExitNodes[nonExitNodes.length - 1]
            : nodes.find((n) => n.id === 'home');

        // Find exit node
        let exitNode = nodes.find((n) => n.data.isExit || n.id === 'exit');
        if (!exitNode) {
          exitNode = {
            id: 'exit',
            type: 'custom',
            data: {
              label: 'Exit',
              type: 'exit',
              id: 'exit',
              isExit: true,
              onNodeClick: (node) => setSelectedNode(node),
            },
            position: { x: 0, y: 0 },
          };
        }

        if (lastNode && exitNode) {
          // Remove existing edge from last node to exit
          const filteredEdges = edges.filter(
            (e) => !(e.source === lastNode.id && e.target === exitNode!.id)
          );

          const edgeToNew: Edge = {
            id: `e${lastNode.id}-${newNode.id}`,
            source: lastNode.id,
            target: newNode.id,
            style: {
              stroke: '#D000E5',
              strokeWidth: 2,
              filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))',
            },
            animated: true,
          };

          const edgeToExit: Edge = {
            id: `e${newNode.id}-${exitNode.id}`,
            source: newNode.id,
            target: exitNode.id,
            style: {
              stroke: '#D000E5',
              strokeWidth: 2,
              filter: 'drop-shadow(0 0 5px rgba(208, 0, 229, 0.7))',
            },
            animated: true,
          };

          const hasExit = nodes.some((n) => n.id === exitNode!.id);
          const updatedNodes = hasExit
            ? [...nodes, newNode]
            : [...nodes, newNode, exitNode!];

          const { nodes: layouted } = getLayoutedElements(updatedNodes, [
            ...filteredEdges,
            edgeToNew,
            edgeToExit,
          ]);

          setNodes(layouted);
          setEdges([...filteredEdges, edgeToNew, edgeToExit]);
        }
      },
      [nodes, edges, setNodes, setEdges, isViewMode]
    );

    // Resets the active item when a drag is cancelled
    const handleDragCancel = useCallback(() => {
      if (isViewMode) return;
      setActiveItem(null);
    }, [isViewMode]);

    // Clears the selected node when clicking outside nodes
    const onPaneClick = useCallback(() => {
      setSelectedNode(null);
    }, []);

    return (
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-4 px-4 pb-4">
          {/* Sidebar - Hidden in view mode */}
          {!isViewMode && (
            <div className="w-60 bg-white border rounded-lg p-4 flex-shrink-0 shadow-md">
              <div className="font-semibold mb-2 text-[#3F5265] text-[16px] pb-4 border-b border-gray-300">
                Flow Elements
              </div>
              <div className="text-xs text-gray-500 mb-2 pt-2">Action</div>
              <SidebarItem type="send" label="Send Email" />
              <SidebarItem type="tags" label="Tags" />
              <div className="text-xs text-gray-500 my-2 pt-2">Delay</div>
              <SidebarItem type="wait" label="Wait Until" />
              <div className="text-xs text-gray-500 my-2 pt-2">
                Flow Condition
              </div>
              <SidebarItem type="branch" label="True/False Branch" />
              <SidebarItem type="multi" label="Multi Split" />
              <SidebarItem type="percent" label="Percentage Split" />
            </div>
          )}

          {/* Canvas - Full width in view mode */}
          <div className={isViewMode ? 'flex-1' : ''}>
            <DroppableCanvas id="reactflow-canvas">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={isViewMode ? undefined : onNodesChange}
                onEdgesChange={isViewMode ? undefined : onEdgesChange}
                onConnect={onConnect}
                onNodeClick={(e, node) => setSelectedNode(node)}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                defaultViewport={{ x: 220, y: 50, zoom: 0.9 }}
                nodesDraggable={!isViewMode}
                nodesConnectable={!isViewMode}
                elementsSelectable={!isViewMode}
                fitView
                fitViewOptions={{ padding: 0.2 }}
              >
                <Controls />
                <Background color="#aaa" gap={16} />
              </ReactFlow>
            </DroppableCanvas>
          </div>

          {/* Detail Panel - Always visible */}
          <div className="w-72 h-[650px] bg-white border rounded-lg p-4 flex-shrink-0 shadow-md overflow-y-auto">
            {selectedNode ? (
              <DetailPanel
                node={selectedNode as any}
                onUpdateNode={
                  isViewMode
                    ? undefined
                    : (updatedNode) => {
                      setNodes((nds) =>
                        nds.map((n) =>
                          n.id === updatedNode.id
                            ? { ...n, data: updatedNode.data }
                            : n
                        )
                      );
                    }
                }
                readOnly={isViewMode}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                {isViewMode
                  ? 'Select a node to view its details'
                  : 'Select a node to edit'}
              </div>
            )}
          </div>

          {/* Drag Overlay - Hidden in view mode */}
          {!isViewMode && (
            <DragOverlay>
              {activeItem ? (
                <div className="p-2 bg-white rounded border shadow-lg cursor-grabbing text-sm">
                  {activeItem.label}
                </div>
              ) : null}
            </DragOverlay>
          )}
        </div>
      </DndContext>
    );
  }
);

export default HierarchyGraphFlow;